"use client";

import { AnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import { TimeZonePicker } from "@/components/tailwind/ui/timezone-picker";
import { useUserTimeZone } from "@/hooks/use-user-timezone";
import { toast } from "@/lib/toast";
import { Check } from "lucide-react";
import { useTheme } from "next-themes";
import { useRef, useState } from "react";
import { appearances } from "../../ui/menu";
import ToggleSetting from "../components/ToggleSetting";

export default function PreferencesContent() {
  const { theme: currentTheme, setTheme } = useTheme();
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const appearanceTriggerRef = useRef<HTMLButtonElement>(null);
  const { timeZone, autoTimeZone, isLoading, isSaving, isUnavailable, setTimeZone, setAutoTimeZone } =
    useUserTimeZone();

  const handleAutoChange = (checked: boolean) => {
    setAutoTimeZone(checked).catch((err: Error) => toast.error(err.message));
  };
  const handleZoneSelect = (zone: string) => {
    setTimeZone(zone).catch((err: Error) => toast.error(err.message));
  };

  // Find the currently active theme object to display its name and icon
  const activeAppearance =
    appearances.find((a) => a.theme.toLowerCase() === currentTheme?.toLowerCase()) || appearances[1];

  return (
    <div className="space-y-12">
      <div className="border-b border-component mb-4 mt-0 pb-3 text-base font-medium w-auto text-zinc-900 dark:text-zinc-100">
        Preferences
      </div>

      {/* Appearance */}
      <div className="flex flex-wrap items-center justify-between gap-3 cursor-default">
        <div className="flex flex-col gap-1.5 min-w-0 flex-1 sm:flex-none sm:w-3/4 sm:mr-[5%]">
          <div className="text-zinc-900 dark:text-zinc-100 text-sm leading-5 font-normal">
            <div className="flex flex-row gap-1">Appearance</div>
          </div>
          <div className="text-zinc-600 dark:text-zinc-400 text-[13px] leading-[18px] font-normal">
            Customize how Closot looks on your device.
          </div>
        </div>

        <button
          ref={appearanceTriggerRef}
          aria-expanded={appearanceOpen}
          onClick={() => setAppearanceOpen(!appearanceOpen)}
          className="user-select-none transition-colors duration-200 ease-in cursor-pointer inline-flex items-center h-8 px-3 rounded-md whitespace-nowrap text-sm justify-center font-medium leading-[1.2] border border-zinc-300 dark:border-zinc-600 hover-component  gap-2 text-zinc-900 dark:text-zinc-100 min-w-[100px]"
        >
          {activeAppearance?.theme || "Light"}
          <svg
            aria-hidden="true"
            role="graphics-symbol"
            viewBox="0 0 16 16"
            className="w-3.5 h-3.5 block fill-zinc-500 dark:fill-zinc-400 flex-shrink-0"
          >
            <path d="m12.76 6.52-4.32 4.32a.62.62 0 0 1-.44.18.62.62 0 0 1-.44-.18L3.24 6.52a.63.63 0 0 1 0-.88c.24-.24.64-.24.88 0L8 9.52l3.88-3.88c.24-.24.64-.24.88 0s.24.64 0 .88" />
          </svg>
        </button>
        {appearanceOpen && (
          <AnchoredPopover
            anchor={appearanceTriggerRef}
            onClose={() => setAppearanceOpen(false)}
            placement="bottom-end"
            offsetPx={8}
            className="w-48 rounded-xl border border-component bg-modal p-1 text-popover-foreground shadow-lg outline-none z-[10000]"
          >
            <div className="space-y-1">
              {appearances.map(({ theme, icon }) => (
                <button
                  key={theme}
                  className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm transition-colors ${
                    currentTheme === theme.toLowerCase()
                      ? "bg-selected text-gray-900 dark:text-gray-100"
                      : "hover-component text-gray-700 dark:text-gray-300"
                  }`}
                  onClick={() => setTheme(theme.toLowerCase())}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-gray-500 dark:text-gray-400">{icon}</div>
                    <span>{theme}</span>
                  </div>
                  {currentTheme === theme.toLowerCase() && <Check className="h-4 w-4 text-primary" />}
                </button>
              ))}
            </div>
          </AnchoredPopover>
        )}
      </div>

      <div className="pt-12">
        <div className="border-b border-component mb-4 mt-0 pb-3 text-base font-medium w-auto text-zinc-900 dark:text-zinc-100">
          Language & Time
        </div>

        {/* Language */}
        <div className="flex flex-col items-start w-auto h-auto px-0">
          <div className="flex items-start w-full">
            <div className="flex flex-col gap-1.5 w-full">
              <div className="text-zinc-900 dark:text-zinc-100 text-sm leading-5 font-normal">Language</div>
              <div className="text-zinc-600 dark:text-zinc-400 text-[13px] leading-[18px] font-normal">
                Change the language used in the user interface.
              </div>
            </div>
            <div className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md whitespace-nowrap text-sm flex-shrink-0 leading-[1.2] min-w-0 text-zinc-900 dark:text-zinc-100 transition-colors opacity-50 cursor-not-allowed pointer-events-none">
              English (US)
              <svg
                aria-hidden="true"
                role="graphics-symbol"
                viewBox="0 0 16 16"
                className="w-3.5 h-3.5 block fill-zinc-500 dark:fill-zinc-400 flex-shrink-0"
              >
                <path d="m12.76 6.52-4.32 4.32a.62.62 0 0 1-.44.18.62.62 0 0 1-.44-.18L3.24 6.52a.63.63 0 0 1 0-.88c.24-.24.64-.24.88 0L8 9.52l3.88-3.88c.24-.24.64-.24.88 0s.24.64 0 .88" />
              </svg>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center pointer-events-none w-full h-[18px] flex-shrink-0">
          <div role="separator" className="w-full h-px invisible border-b border-zinc-300 dark:border-zinc-600" />
        </div>

        {/* Start week on Monday */}
        {/* <ToggleSetting
            label="Start week on Monday"
            description="This will change how all calendars in your app look."
            checked={false}
          /> */}

        <div className="flex items-center justify-center pointer-events-none w-full h-[18px] flex-shrink-0">
          <div role="separator" className="w-full h-px invisible border-b border-zinc-300 dark:border-zinc-600" />
        </div>

        {/* Set timezone automatically */}
        <ToggleSetting
          label="Set time zone automatically using your location"
          description="Dates and times across the app are shown in this time zone."
          checked={autoTimeZone}
          onChange={handleAutoChange}
        />

        <div className="flex items-center justify-center pointer-events-none w-full h-[18px] flex-shrink-0">
          <div role="separator" className="w-full h-px invisible border-b border-zinc-300 dark:border-zinc-600" />
        </div>

        {/* Timezone */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
          <div className="flex flex-col gap-1.5 min-w-0 flex-1 sm:flex-none sm:w-3/4 sm:mr-[5%]">
            <div className="text-zinc-900 dark:text-zinc-100 text-sm leading-5 font-normal">
              <div className="flex flex-row gap-1">Time zone</div>
            </div>
            <div className="text-zinc-600 dark:text-zinc-400 text-[13px] leading-[18px] font-normal">
              {autoTimeZone
                ? "Following this device. Turn off automatic to choose a different one."
                : "Choose the time zone dates and times are shown in."}
            </div>
          </div>
          <TimeZonePicker
            value={timeZone}
            onSelect={handleZoneSelect}
            disabled={autoTimeZone || isSaving}
            placeholder="Detecting…"
          />
        </div>

        {isUnavailable && !isLoading && (
          <div className="mt-2 text-[13px] leading-[18px] text-zinc-600 dark:text-zinc-400">
            Your time zone could not be saved, so dates are shown using this device. Times you see are still correct —
            the choice just won't follow you to another device yet.
          </div>
        )}
      </div>

      <div className="flex items-center justify-center pointer-events-none w-full h-12 flex-shrink-0">
        <div role="separator" className="w-full h-px invisible border-b border-zinc-300 dark:border-zinc-600" />
      </div>

      {/* Desktop app section */}
      {/* <div>
        <div className="border-b border-component mb-4 mt-0 pb-3 text-base font-medium w-auto text-zinc-900 dark:text-zinc-100">
          Desktop app
        </div>

        <ToggleSetting
          label="Open links in desktop app"
          description={
            <>
              You must have the{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                className="inline text-current underline select-none cursor-pointer"
              >
                macOS app
              </a>{" "}
              installed.
            </>
          }
          checked={false}
        />

        <div className="flex items-center justify-center pointer-events-none w-full h-[18px] flex-shrink-0">
          <div
            role="separator"
            className="w-full h-px invisible border-b border-zinc-300 dark:border-zinc-600"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 cursor-default">
          <div className="flex flex-col gap-1.5 min-w-0 flex-1 sm:flex-none sm:w-3/4 sm:mr-[5%]">
            <div className="text-zinc-900 dark:text-zinc-100 text-sm leading-5 font-normal">
              <div className="flex flex-row gap-1"></div>
            </div>
            <div className="text-zinc-600 dark:text-zinc-400 text-[13px] leading-[18px] font-normal">
              If installed, macOS will open links to Closot in the desktop app, even if this
              setting is turned off. To disable that behavior, enable "Open Closot links in browser"
              in your app.
            </div>
          </div>
          <button
            role="button"
            tabIndex={0}
            className="user-select-none transition-colors duration-200 ease-in cursor-pointer inline-flex items-center h-8 px-3 rounded-md whitespace-nowrap text-sm justify-center font-medium leading-[1.2] border border-zinc-300 dark:border-zinc-600 hover-component "
          >
            Set in app
          </button>
        </div>

        <div className="flex items-center justify-center pointer-events-none w-full h-[18px] flex-shrink-0">
          <div
            role="separator"
            className="w-full h-px invisible border-b border-zinc-300 dark:border-zinc-600"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 cursor-default">
          <div className="flex flex-col gap-1.5 min-w-0 flex-1 sm:flex-none sm:w-3/4 sm:mr-[5%]">
            <div className="text-zinc-900 dark:text-zinc-100 text-sm leading-5 font-normal">
              <div className="flex flex-row gap-1">Open on start</div>
            </div>
            <div className="text-zinc-600 dark:text-zinc-400 text-[13px] leading-[18px] font-normal">
              Choose what to show when Books by ReventLabs  starts or when you switch workspaces.
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md whitespace-nowrap text-sm flex-shrink-0 leading-[1.2] min-w-0 text-zinc-900 dark:text-zinc-100 cursor-pointer hover-component  transition-colors">
            Last visited page
            <svg
              aria-hidden="true"
              role="graphics-symbol"
              viewBox="0 0 16 16"
              className="w-3.5 h-3.5 block fill-zinc-500 dark:fill-zinc-400 flex-shrink-0"
            >
              <path d="m12.76 6.52-4.32 4.32a.62.62 0 0 1-.44.18.62.62 0 0 1-.44-.18L3.24 6.52a.63.63 0 0 1 0-.88c.24-.24.64-.24.88 0L8 9.52l3.88-3.88c.24-.24.64-.24.88 0s.24.64 0 .88" />
            </svg>
          </div>
        </div>
      </div> */}

      {/* <div className="flex items-center justify-center pointer-events-none w-full h-12 flex-shrink-0">
        <div
          role="separator"
          className="w-full h-px invisible border-b border-zinc-300 dark:border-zinc-600"
        />
      </div> */}

      {/* Privacy section */}
      {/* <div>
        <div className="border-b border-component mb-4 mt-0 pb-3 text-base font-medium w-auto text-zinc-900 dark:text-zinc-100">
          Privacy
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 cursor-default">
          <div className="flex flex-col gap-1.5 min-w-0 flex-1 sm:flex-none sm:w-3/4 sm:mr-[5%]">
            <div className="text-zinc-900 dark:text-zinc-100 text-sm leading-5 font-normal">
              <div className="flex flex-row gap-1">Cookie settings</div>
            </div>
            <div className="text-zinc-600 dark:text-zinc-400 text-[13px] leading-[18px] font-normal">
              Customize cookies. See{" "}
              <a
                href="#"
                rel="noopener noreferrer"
                className="inline text-current underline select-none cursor-pointer"
              >
                Cookie Notice
              </a>{" "}
              for details.
            </div>
          </div>
          <div className="inline-flex items-center h-8 px-2 rounded-md whitespace-nowrap text-sm flex-shrink-0 leading-[1.2] min-w-0 text-zinc-900 dark:text-zinc-100 cursor-pointer hover-component  transition-colors">
            Customize
            <svg
              aria-hidden="true"
              role="graphics-symbol"
              viewBox="0 0 30 30"
              className="w-2.5 h-full block fill-zinc-500 dark:fill-zinc-400 flex-shrink-0 ml-1 transition-transform duration-200"
            >
              <polygon points="15,17.4 4.8,7 2,9.8 15,23 28,9.8 25.2,7 " />
            </svg>
          </div>
        </div>

        <div className="flex items-center justify-center pointer-events-none w-full h-[18px] flex-shrink-0">
          <div
            role="separator"
            className="w-full h-px invisible border-b border-zinc-300 dark:border-zinc-600"
          />
        </div>

        <ToggleSetting
          label="Show my view history"
          description={
            <>
              People with edit or full access will be able to see when you've viewed a page.{" "}
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="inline text-current underline select-none cursor-pointer"
              >
                Learn more
              </a>
              .
            </>
          }
          checked={true}
        />

        <div className="flex items-center justify-center pointer-events-none w-full h-[18px] flex-shrink-0">
          <div
            role="separator"
            className="w-full h-px invisible border-b border-zinc-300 dark:border-zinc-600"
          />
        </div>

        <ToggleSetting
          label="Profile discoverability"
          description={
            <>
              Users with your email can see your name and profile picture when inviting you to a
              new workspace.{" "}
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="inline text-current underline select-none cursor-pointer"
              >
                Learn more
              </a>
              .
            </>
          }
          checked={true}
        />
      </div> */}
    </div>
  );
}
